import { useEffect, useRef } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PDFRenderer } from "@/domains/documents/components/PDFRenderer";

interface ProtocolPreviewDialogProps {
  /** The rendered PDF, or null when the preview is closed. The bytes travel
   * beside the URL because the tab escape hatch needs a URL of its own. */
  preview: { url: string; blob: Blob; filename: string } | null;
  title: string;
  onClose: () => void;
}

/**
 * The rendered protocol, shown in a modal over the export dialog.
 *
 * Deliberately not a new tab (which is what the /documents template preview
 * does): the user stays inside the wizard, so a look at the PDF costs nothing
 * and the selection behind it is untouched. The tab is still offered as an
 * escape hatch — a browser that refuses to render a PDF inside an iframe
 * (mobile Safari, Chrome on Android) shows a blank frame and fires no `error`
 * event, so PDFRenderer's fallback never appears and the always-visible action
 * bar is the only way through.
 *
 * Stacking: both dialogs' overlay and content are `z-50`, and portals mount in
 * open order, so this one already paints above the wizard. The explicit z-[60]
 * is insurance against another portal landing between them, and documents the
 * intent. Radix keeps a layer stack, so Escape dismisses only this dialog and
 * returns focus to the Preview button.
 */
export function ProtocolPreviewDialog({ preview, title, onClose }: ProtocolPreviewDialogProps) {
  // The tab reads its URL for as long as it likes, so its copy outlives the
  // click — but not this preview. Revoking them together bounds the cost at one
  // live PDF per preview; revoking none pinned a whole document in memory for
  // every Open-in-tab press.
  const tabUrls = useRef<string[]>([]);
  useEffect(
    () => () => {
      tabUrls.current.forEach((url) => URL.revokeObjectURL(url));
      tabUrls.current = [];
    },
    [preview]
  );

  return (
    <Dialog
      open={preview !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        overlayClassName="z-[60]"
        className="top-[50dvh] z-[60] flex h-[calc(100dvh-3rem)] max-h-none w-[calc(100vw-3rem)] max-w-none flex-col overflow-hidden"
      >
        <DialogHeader className="shrink-0 pb-2">
          <DialogTitle className="text-base font-medium">{title}</DialogTitle>
          {/* The filename is the description Radix wants anyway, and it tells
              the user what Save would actually write. */}
          <DialogDescription className="text-xs">{preview?.filename}</DialogDescription>
        </DialogHeader>
        {preview && (
          <PDFRenderer
            docId="protocol-preview"
            blobUrl={preview.url}
            filename={preview.filename}
            // A tab we do not own gets a URL of its own. Handing it this
            // dialog's URL means revoking it out from under a tab that may
            // still be loading it — a blank tab, and the bigger the document
            // or the slower the machine the likelier it is. So the tab's copy
            // is kept until the preview closes, and the tab keeps whatever it
            // has already rendered.
            // A blocked pop-up returns null and shows the user nothing, and
            // this button is the way out of a frame that would not render the
            // PDF — so the bytes are downloaded instead, as
            // `openTemplateFormPdf` does on the same dead end. Nothing reads
            // that URL after the click, so it goes back straight away.
            onOpenInTab={() => {
              const tabUrl = URL.createObjectURL(preview.blob);
              if (window.open(tabUrl, "_blank")) {
                tabUrls.current.push(tabUrl);
                return;
              }
              try {
                downloadBlobUrl(tabUrl, preview.filename);
              } finally {
                URL.revokeObjectURL(tabUrl);
              }
            }}
            onDownload={() => downloadBlobUrl(preview.url, preview.filename)}
            className="h-full flex-1"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Save the previewed bytes without re-rendering them. The URL is owned by
 * whoever minted it — the wizard for the preview, the tab hatch for its own
 * copy — so this must not revoke, and the anchor has to be in the document,
 * because Firefox ignores a click on a detached one.
 */
function downloadBlobUrl(url: string, filename: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
  }
}
